import React, { useEffect, useState } from 'react';
import { Container, Card, Form, Button, Alert, Row, Col, InputGroup, Table, Badge } from 'react-bootstrap';
import { authAPI } from '../../utils/httpClient';
import { useI18n } from '../../i18n';

const InvitationManager = () => {
  const [adminUsername, setAdminUsername] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [listLoading, setListLoading] = useState(false);
  const [activeInvites, setActiveInvites] = useState([]);
  const [stats, setStats] = useState(null);
  const { t } = useI18n();

  const loadData = async () => {
    setListLoading(true);
    try {
      const [listResp, statsResp] = await Promise.all([
        authAPI.getActiveInvitations(),
        authAPI.getInvitationStats(),
      ]);
      if (listResp.success) setActiveInvites(listResp.data || []);
      if (statsResp.success) setStats(statsResp.data || null);
    } catch (e) {
      // silent
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setResult(null);
    setLoading(true);
    try {
      const resp = await authAPI.requestInvitationCode({
        admin_username: adminUsername,
        admin_password: adminPassword,
        email: recipientEmail || undefined,
      });

      if (resp.success) {
        setResult({ code: resp.code, expires_at: resp.expires_at, message: resp.message });
        // refresh lists
        loadData();
      } else {
        setError(resp.error?.message || resp.message || '生成邀请码失败');
      }
    } catch (err) {
      setError(err?.message || '请求失败');
    } finally {
      setLoading(false);
    }
  };

  const copyCode = async () => {
    if (!result?.code) return;
    try {
      await navigator.clipboard.writeText(result.code);
      alert('已复制邀请码到剪贴板');
    } catch {
      // ignore
    }
  };

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col md={8} lg={6}>
          <Card className="p-4">
            <h3 className="mb-4">{t('invitation:title')}</h3>
            <p className="text-muted">{t('invitation:subtitle')}</p>
            {error && <Alert variant="danger">{typeof error === 'string' ? error : (error?.message || JSON.stringify(error) || '邀请错误')}</Alert>}
            {result && (
              <Alert variant="success">
                <div className="mb-2">{t('invitation:result_message', { message: result.message })}</div>
                <div className="d-flex align-items-center">
                  <strong className="me-2">{t('invitation:code_label')}</strong>
                  <code className="me-2">{result.code}</code>
                  <Button size="sm" variant="outline-secondary" onClick={copyCode}>{t('common:actions.copy', { defaultValue: 'Copy' })}</Button>
                </div>
                {result.expires_at && (<div className="mt-2">{t('invitation:table.expires')}: {result.expires_at}</div>)}
              </Alert>
            )}
            <Form onSubmit={onSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>{t('invitation:admin_username')}</Form.Label>
                <Form.Control
                  type="text"
                  value={adminUsername}
                  onChange={(e) => setAdminUsername(e.target.value)}
                  placeholder={t('invitation:admin_username')}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>{t('invitation:admin_password')}</Form.Label>
                <Form.Control
                  type="password"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  placeholder={t('invitation:admin_password')}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-4">
                <Form.Label>{t('invitation:recipient_email')}</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="email"
                    value={recipientEmail}
                    onChange={(e) => setRecipientEmail(e.target.value)}
                    placeholder={t('invitation:recipient_email')}
                  />
                </InputGroup>
              </Form.Group>
              <div className="d-grid">
                <Button type="submit" variant="primary" disabled={loading}>
                  {loading ? t('invitation:generating') : t('invitation:generate')}
                </Button>
              </div>
            </Form>
            <hr className="my-4" />
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">{t('invitation:list_title')}</h5>
              <Button size="sm" variant="outline-secondary" onClick={loadData} disabled={listLoading}>{t('common:actions.refresh')}</Button>
            </div>
            {stats && (
              <div className="mb-3">
                <Badge bg="secondary" className="me-2">{t('invitation:stats.total', { val: stats.total_created })}</Badge>
                <Badge bg="success" className="me-2">{t('invitation:stats.active', { val: stats.active_codes })}</Badge>
                <Badge bg="warning" className="me-2">{t('invitation:stats.used', { val: stats.total_used })}</Badge>
                <Badge bg="danger">{t('invitation:stats.expired', { val: stats.total_expired })}</Badge>
              </div>
            )}
            <div className="table-responsive">
              <Table striped bordered hover size="sm">
                <thead>
                  <tr>
                    <th>{t('invitation:table.code')}</th>
                    <th>{t('invitation:table.creator')}</th>
                    <th>{t('invitation:table.status')}</th>
                    <th>{t('invitation:table.expires')}</th>
                    <th>{t('invitation:table.created')}</th>
                  </tr>
                </thead>
                <tbody>
                  {activeInvites.length === 0 && (
                    <tr><td colSpan={5} className="text-center text-muted">{t('invitation:table.no_data')}</td></tr>
                  )}
                  {activeInvites.map((it) => (
                    <tr key={it.code}>
                      <td><code>{it.code}</code></td>
                      <td>{it.created_by ?? '-'}</td>
                      <td>{it.is_used ? t('invitation:status_value.used') : t('invitation:status_value.unused')}</td>
                      <td>{it.expires_at || '-'}</td>
                      <td>{it.created_at || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default InvitationManager;
